import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SongType } from 'services/api/entities';
import { selectRelease } from 'services/store/distribution/selectors';
import {
  ReleaseFieldType,
  ReleaseSongFieldType,
  SongData,
} from 'services/store/distribution/types';

const useTracklistValidation = (releaseId: string) => {
  const release = useSelector(selectRelease(releaseId));

  const isMetadataValid = (song: SongData) => {
    const { metadata } = song;

    const composer = metadata[ReleaseSongFieldType.ComposerName];
    if (!composer.data || composer.error || composer.loading) {
      return false;
    }

    const hasVocals = metadata[ReleaseSongFieldType.HasVocals];
    if (hasVocals.error || hasVocals.loading) {
      return false;
    }
    if (hasVocals.data) {
      const vocalsContent = metadata[ReleaseSongFieldType.VocalsContent];
      if (!vocalsContent.data || vocalsContent.error || vocalsContent.loading) {
        return false;
      }

      const language = metadata[ReleaseSongFieldType.Language];
      if (!language.data || language.error || language.loading) {
        return false;
      }

      const vocalist = metadata[ReleaseSongFieldType.VocalistName];
      if (vocalist.error || vocalist.loading) {
        return false;
      }
    }

    const additionalArtists = metadata[ReleaseSongFieldType.AdditionalArtists];
    if (additionalArtists.error || additionalArtists.loading) {
      return false;
    }

    const remixers = metadata[ReleaseSongFieldType.SongRemixers];
    if (remixers.error || remixers.loading) {
      return false;
    }

    const producers = metadata[ReleaseSongFieldType.SongProducers];
    if (producers.error || producers.loading) {
      return false;
    }

    return true;
  };

  const isValid = useMemo(() => {
    if (!release) {
      return false;
    }

    /* TODO: Modify this code in STR-3466
       Reason: The SongTitle should be in the ReleaseSongFieldType but BE is not ready yet
     */
    const title = release[ReleaseFieldType.SongTitle];
    if (!title.data || title.error || title.loading) {
      return false;
    }

    const { songs } = release;
    return songs
      .filter((song) => song.songType === SongType.USER_UPLOADED)
      .every(isMetadataValid);
  }, [release]);

  return {
    isValid,
  };
};

export default useTracklistValidation;
